import {
  Button
} from "@mui/material";
import { Formik } from "formik";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import ExportButton from "../commons/export/ExportButton";
import FormikDatePicker from "../commons/formik/FormikDatePicker";
import { tableConfig, tableStyles } from "../commons/table/tableConfig";
import TitleContainer from "../commons/table/utilsTable/TitleWithFetchData";
import { StyledCuentaCorrienteTable } from "../styled/CuentaCorriente";
import CuentaCorrienteAccountData from "./CuentaCorrienteAccountData";
import ReporteEstadoButton from "./ReporteEstadoButton";

const initialValues = {
  fechaDesde: null,
  fechaHasta: null,
};

const validationSchema = Yup.object().shape({
  fechaDesde: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaDesde",
      message: "La fecha desde debe ser menor o igual a la fecha hasta",
      test: function (value) {
        if (!value || !this.parent.fechaHasta) {
          return true;
        }
        return value <= this.parent.fechaHasta;
      },
    }),

  fechaHasta: Yup.date()
    .nullable()
    .typeError("Fecha inválida")
    .test({
      name: "fechaHasta",
      message: "La fecha hasta debe ser mayor o igual a la fecha desde",
      test: function (value) {
        if (!value || !this.parent.fechaDesde) {
          return true;
        }
        return value >= this.parent.fechaDesde;
      },
    }),
});

const CuentaCorrienteTable = ({
  title,
  data,
  loading,
  fetchData,
  noRefresh,
  secondaryTitle,
  handleFilter,
  fechaDesde,
  fechaHasta,
  selectedTab,
  balanceAtDate,
}) => {
  const [filterModel, setfilterModel] = useState(initialValues);
  const [formResetKey, setFormResetKey] = useState(Date.now());

  const formatNumber = (number) => {
    return new Intl.NumberFormat("es-AR").format(number || 0);
  }

  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      setfilterModel({
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
      });
    }
  }, [fechaDesde, fechaHasta]);

  const columns = [
    {
      header: "Periodo Fiscal",
      accessorKey: "periodoFiscal",

    },
    {
      header: "Periodo Cobertura",
      accessorKey: "periodoCobertura",

    },
    {
      header: "Cápitas",
      accessorKey: "capitas",
    },
    {
      header: "Masa Salarial",
      accessorKey: "masaSalarial",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
      size: 110,
      maxSize: 110,
    },
    {
      header: "Suma Fija Declarada",
      accessorKey: "sumaFijaDeclarada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),

    },
    {
      header: "Alícuota Declarada",
      accessorKey: "alicuotaDeclarada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Suma Fija Facturada",
      accessorKey: "sumaFijaFacturada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Alícuota Facturada",
      accessorKey: "alicuotaFacturada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Valor FFEP",
      accessorKey: "tasaFFEP",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Importe FFEP Devengado",
      accessorKey: "ffepDesvengada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Pago FFEP",
      accessorKey: "pagoFFEP",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Cuota Devengada",
      accessorKey: "cuotaDesvengada",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
      size: 110,
    },
    {
      header: "Pago Cuota",
      accessorKey: "pagoCuota",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
      size: 110,
    },
    {
      header: "Saldo Período",
      accessorKey: "saldoPeriodo",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Saldo Acumulado",
      accessorKey: "saldoTotal",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Intereses por Mora Devengados",
      accessorKey: "interesMora",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
      maxSize: 80,
    },
    {
      header: "Intereses Pagados",
      accessorKey: "interesPagado",
      filterFn: (row, id, filterValue) => {
        if (!filterValue) return true;
        return String(row.getValue(id) ?? "").includes(String(filterValue));
      },
      Cell: ({ cell }) => formatNumber(cell.getValue()),
    },
    {
      header: "Base",
      accessorKey: "base",
    }
  ];

  const table = useMaterialReactTable({
    ...tableConfig,
    columns,
    data: data,
    layoutMode: "grid",
    state: { isLoading: loading },
    style: { ...tableStyles.container },
    enableColumnActions: false,
    renderToolbarInternalActions: () => (
      <CuentaCorrienteAccountData />
    ),
    renderTopToolbarCustomActions: ({ table }) => (
      <>
        <div>
          <StyledCuentaCorrienteTable.HeaderRow>
            <StyledCuentaCorrienteTable.ToolbarActionsContainer>
              <ExportButton data={data} columns={columns} title="Cuenta Corriente" />
              <ReporteEstadoButton tipoContrato={selectedTab ?? undefined} />
            </StyledCuentaCorrienteTable.ToolbarActionsContainer>
            <TitleContainer
              title={title}
              noRefresh={noRefresh}
              fetchData={handleRefresh}
              saldoALaFecha={balanceAtDate}
              subTitle={secondaryTitle}
            />
          </StyledCuentaCorrienteTable.HeaderRow>
          <Formik
            key={formResetKey}
            initialValues={filterModel}
            validationSchema={validationSchema}
            onSubmit={handleFilter}
            enableReinitialize={true}
          >
            {({ values }) => (
              <StyledCuentaCorrienteTable.FilterContainer>
                <StyledCuentaCorrienteTable.FilterItem>
                  <FormikDatePicker
                    name="fechaDesde"
                    label="Fecha desde"
                    ampm="false"
                    format="dd/MM/yyyy"
                    variant="inline"
                  />
                </StyledCuentaCorrienteTable.FilterItem>
                <StyledCuentaCorrienteTable.FilterItem>
                  <FormikDatePicker
                    name="fechaHasta"
                    label="Fecha hasta"
                    ampm="false"
                    format="dd/MM/yyyy"
                    variant="inline"
                  />
                </StyledCuentaCorrienteTable.FilterItem>
                <StyledCuentaCorrienteTable.ButtonContainer>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    disabled={
                      values.fechaDesde == null ||
                      values.fechaHasta == null
                    }
                  >
                    Buscar
                  </Button>
                </StyledCuentaCorrienteTable.ButtonContainer>
              </StyledCuentaCorrienteTable.FilterContainer>
            )}
          </Formik>
        </div>
      </>
    ),
  });

  const handleRefresh = () => {
    setfilterModel(initialValues);
    setFormResetKey(Date.now());
    table.resetColumnFilters();
    fetchData();
  };

  return (
    <div>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default CuentaCorrienteTable;
